import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, User } from "lucide-react";
import api from "../lib/api";
import { Input } from "./ui/input";

export default function QuickSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/clienti`, { params: { q } });
        setResults(data.slice(0, 8));
        setOpen(true);
      } catch {}
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const go = (id) => {
    setOpen(false);
    setQ("");
    navigate(`/clienti/${id}`);
  };

  return (
    <div className="relative w-full max-w-md" ref={boxRef}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <Input
        ref={inputRef}
        data-testid="quick-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => q && setOpen(true)}
        placeholder="Cerca cliente per nome, cognome, telefono, email…  (Ctrl+K)"
        className="pl-9 pr-4 h-10 bg-white border-slate-200"
      />
      {open && results.length > 0 && (
        <div data-testid="quick-search-results" className="absolute z-50 mt-2 w-full bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          {results.map((c) => (
            <button
              key={c.id}
              data-testid={`quick-search-result-${c.id}`}
              onClick={() => go(c.id)}
              className="w-full text-left px-4 py-2.5 hover:bg-slate-50 flex items-center gap-3 border-b border-slate-100 last:border-0"
            >
              <div className="h-8 w-8 rounded-full bg-[#1E40AF]/10 flex items-center justify-center">
                <User className="h-4 w-4 text-[#1E40AF]" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{c.cognome} {c.nome}</div>
                <div className="text-xs text-slate-500 truncate">{c.telefono || "—"} · {c.email || "—"}</div>
              </div>
            </button>
          ))}
        </div>
      )}
      {open && q && results.length === 0 && (
        <div className="absolute z-50 mt-2 w-full bg-white border border-slate-200 rounded-xl shadow-lg px-4 py-3 text-sm text-slate-500">
          Nessun cliente trovato
        </div>
      )}
    </div>
  );
}
